'use client';

/**
 * QUICK CONTACT MODAL
 *
 * Lightweight inline modal for rapidly creating a contact with optional
 * contact person and location. Designed to be embedded in any form that
 * has a contact selector, so users don't need to leave the current flow
 * to create a new stakeholder.
 *
 * Uses LocationAutocomplete for address resolution with proper coordinates.
 *
 * API flow:
 *   1. POST /api/v1/contacts/           → create contact
 *   2. POST /api/v1/contacts/{id}/people/   → create person (optional)
 *   3. POST /api/v1/contacts/{id}/locations → create location (optional)
 */

import { useState, useCallback } from 'react';
import { X } from 'lucide-react';
import {
  Building2,
  User,
  MapPin,
  Loader2,
  Plus,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle2,
  CreditCard,
} from 'lucide-react';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';
import LocationAutocomplete, { type CustomLocationData } from '@/components/maps/LocationAutocomplete';
import { emitGuidedTourTrainingEvent } from '@/components/training/guided-tour';

// ============================================================================
// TYPES
// ============================================================================

export interface QuickContactResult {
  contact: {
    id: number;
    contact_number: string;
    company_name: string;
    contact_type: string;
    email: string | null;
    phone: string | null;
  };
  person: {
    id: number;
    first_name: string;
    last_name: string;
    email: string | null;
    phone: string | null;
    job_title: string | null;
  } | null;
  location: {
    id: number;
    location_name: string;
    address: string;
    latitude: number;
    longitude: number;
    location_type: string;
  } | null;
}

export interface QuickContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (result: QuickContactResult) => void;
  /** Pre-select contact type */
  defaultContactType?: 'customer' | 'vendor' | 'supplier' | 'partner' | 'other';
  /** Show only specific contact types */
  contactTypes?: Array<'customer' | 'vendor' | 'supplier' | 'partner' | 'other'>;
  isDark?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function QuickContactModal({
  isOpen,
  onClose,
  onCreated,
  defaultContactType = 'customer',
  contactTypes,
  isDark = false,
}: QuickContactModalProps) {
  // ---- Contact fields ----
  const [contactType, setContactType] = useState<string>(defaultContactType);
  const [companyName, setCompanyName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [taxNumber, setTaxNumber] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');

  // ---- Person fields (collapsible) ----
  const [showPerson, setShowPerson] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [personEmail, setPersonEmail] = useState('');
  const [personPhone, setPersonPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');

  // ---- Location fields (collapsible) ----
  const [showLocation, setShowLocation] = useState(true);
  const [locationName, setLocationName] = useState('');
  const [locationType, setLocationType] = useState('office');
  const [locationSearch, setLocationSearch] = useState('');
  const [locationData, setLocationData] = useState<CustomLocationData | null>(null);
  const [radiusMeters, setRadiusMeters] = useState(100);

  // ---- Supplier payment terms (COD vs Terms) ----
  // COD (cash on delivery) → payment_terms_days = 0
  // Terms → payment_terms_days = 7/14/30/60/90
  // Only shown when contactType === 'supplier'. This is critical for DIN
  // processing where COD suppliers create EXP records and Terms suppliers
  // create BIL records.
  const [paymentTermsMode, setPaymentTermsMode] = useState<'cod' | 'terms'>('cod');
  const [paymentTermsDays, setPaymentTermsDays] = useState<number>(30);

  // ---- State ----
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'form' | 'success'>('form');

  const availableTypes = contactTypes || ['customer', 'vendor', 'supplier', 'partner', 'other'];

  // ---- Styling helpers ----
  const bg = isDark ? 'bg-gray-800' : 'bg-white';
  const cardBg = isDark ? 'bg-gray-700/50' : 'bg-gray-50';
  const border = isDark ? 'border-gray-600' : 'border-gray-300';
  const text = isDark ? 'text-gray-100' : 'text-gray-900';
  const textMuted = isDark ? 'text-gray-400' : 'text-gray-500';
  const inputCls = `w-full px-3 py-2 rounded-lg border ${border} ${isDark ? 'bg-gray-700 text-white placeholder-gray-400' : 'bg-white text-gray-900 placeholder-gray-400'} focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm`;
  const selectCls = `${inputCls} appearance-none`;
  const labelCls = `block text-xs font-medium ${textMuted} mb-1`;

  // ---- Reset ----
  const resetForm = useCallback(() => {
    setContactType(defaultContactType);
    setCompanyName('');
    setContactEmail('');
    setContactPhone('');
    setTaxNumber('');
    setRegistrationNumber('');
    setShowPerson(true);
    setFirstName('');
    setLastName('');
    setPersonEmail('');
    setPersonPhone('');
    setJobTitle('');
    setShowLocation(true);
    setLocationName('');
    setLocationType('office');
    setLocationSearch('');
    setLocationData(null);
    setRadiusMeters(100);
    setPaymentTermsMode('cod');
    setPaymentTermsDays(30);
    setSaving(false);
    setError(null);
    setStep('form');
  }, [defaultContactType]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  // ---- Validation ----
  const isValid = companyName.trim().length > 0;
  const hasPersonData = firstName.trim().length > 0 || lastName.trim().length > 0;
  const hasLocationData = locationData !== null && locationName.trim().length > 0;

  // ---- Submit ----
  const handleSubmit = useCallback(async () => {
    if (!isValid) return;

    // Ask whether the new stakeholder should be active or kept as draft,
    // consistent with StakeholderOnboardingModal and ContactManagementHub.
    let makeActive = true;
    const confirmed = await notify.confirm({
      title: 'Account Status',
      message: `Should this contact be made active now, or kept as draft? Draft contacts are not yet active and cannot transact until activated.`,
      confirmText: 'Make Active',
      cancelText: 'Cancel',
      variant: 'success',
      secondaryConfirmText: 'Keep as Draft',
      secondaryVariant: 'warning',
      onSecondaryConfirm: async () => { makeActive = false; },
    });
    if (!confirmed) return;

    setSaving(true);
    setError(null);

    try {
      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('Not authenticated');

      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // 1. Create contact
      const contactPayload: Record<string, unknown> = {
        contact_type: contactType,
        company_name: companyName.trim(),
        email: contactEmail.trim() || null,
        phone: contactPhone.trim() || null,
        is_active: true,
        account_status: makeActive ? 'active' : 'draft',
      };
      // For suppliers, set payment_terms_days based on the COD/Terms toggle.
      // COD → 0 (cash on delivery), Terms → the selected number of days.
      // This is critical for DIN processing: COD suppliers create EXP records,
      // Terms suppliers create BIL records.
      if (contactType === 'supplier') {
        contactPayload.payment_terms_days = paymentTermsMode === 'cod' ? 0 : paymentTermsDays;
      }
      if (taxNumber.trim()) contactPayload.tax_number = taxNumber.trim();
      if (registrationNumber.trim()) contactPayload.registration_number = registrationNumber.trim();
      // If a person is being added, set contact_person for the legacy field
      if (showPerson && hasPersonData) {
        contactPayload.contact_person = `${firstName.trim()} ${lastName.trim()}`.trim();
      }

      const contactRes = await fetch('/api/v1/contacts/', {
        method: 'POST',
        headers,
        body: JSON.stringify(contactPayload),
      });
      if (!contactRes.ok) {
        const errText = await contactRes.text();
        throw new Error(`Failed to create contact: ${errText}`);
      }
      const contactData = await contactRes.json();
      const createdContact = contactData.data;
      if (!createdContact?.id) throw new Error('Contact creation returned no ID');

      const result: QuickContactResult = {
        contact: {
          id: createdContact.id,
          contact_number: createdContact.contact_number,
          company_name: createdContact.company_name,
          contact_type: createdContact.contact_type,
          email: createdContact.email,
          phone: createdContact.phone,
        },
        person: null,
        location: null,
      };

      // 2. Create person (optional)
      if (showPerson && hasPersonData) {
        try {
          const personPayload: Record<string, unknown> = {
            contact_id: createdContact.id,
            first_name: firstName.trim(),
            last_name: lastName.trim() || firstName.trim(),
            email: personEmail.trim() || null,
            phone: personPhone.trim() || null,
            is_primary: true,
            is_active: true,
          };
          if (jobTitle.trim()) personPayload.job_title = jobTitle.trim();

          const personRes = await fetch(`/api/v1/contacts/${createdContact.id}/people/`, {
            method: 'POST',
            headers,
            body: JSON.stringify(personPayload),
          });
          if (personRes.ok) {
            const personData = await personRes.json();
            const cp = personData.data;
            result.person = {
              id: cp.id,
              first_name: cp.first_name,
              last_name: cp.last_name,
              email: cp.email,
              phone: cp.phone,
              job_title: cp.job_title,
            };
          }
        } catch (e) {
          console.warn('[QuickContact] Person creation failed (non-fatal):', e);
        }
      }

      // 3. Create location (optional)
      if (showLocation && hasLocationData && locationData) {
        try {
          const lat = parseFloat(locationData.lat);
          const lng = parseFloat(locationData.lng);

          // Parse address components from the full address string
          const addressParts = locationData.address.split(',').map(s => s.trim());

          const locationPayload: Record<string, unknown> = {
            contact_id: createdContact.id,
            location_name: locationName.trim(),
            location_type: locationType,
            address_line1: addressParts[0] || locationData.address,
            city: addressParts.length > 2 ? addressParts[addressParts.length - 3] : (addressParts[1] || ''),
            state: addressParts.length > 1 ? addressParts[addressParts.length - 2] : '',
            country: addressParts[addressParts.length - 1] || 'South Africa',
            latitude: lat,
            longitude: lng,
            radius_meters: radiusMeters,
            is_primary: true,
            is_active: true,
          };

          const locRes = await fetch(`/api/v1/contacts/${createdContact.id}/locations`, {
            method: 'POST',
            headers,
            body: JSON.stringify(locationPayload),
          });
          if (locRes.ok) {
            const locData = await locRes.json();
            const cl = locData.data;
            result.location = {
              id: cl.id,
              location_name: cl.location_name || locationName.trim(),
              address: locationData.address,
              latitude: lat,
              longitude: lng,
              location_type: cl.location_type || locationType,
            };
          }
        } catch (e) {
          console.warn('[QuickContact] Location creation failed (non-fatal):', e);
        }
      }

      setStep('success');
      onCreated(result);
      emitGuidedTourTrainingEvent({ type: 'contact_create' });

      // Auto-close after brief success flash
      setTimeout(() => handleClose(), 800);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'An error occurred';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }, [
    isValid, contactType, companyName, contactEmail, contactPhone,
    taxNumber, registrationNumber, showPerson, hasPersonData,
    firstName, lastName, personEmail, personPhone, jobTitle,
    showLocation, hasLocationData, locationData, locationName,
    locationType, radiusMeters, paymentTermsMode, paymentTermsDays,
    onCreated, handleClose,
  ]);

  // ---- Render ----
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-gray-50 dark:bg-gray-900 overflow-y-auto" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <div className="px-2 pb-1 pt-2 sm:px-4 sm:pb-3 sm:pt-4">
        {/* Header */}
        <div className="mb-3 sm:mb-6 px-2 relative">
          <div className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg sm:mb-3 sm:h-16 sm:w-16">
              <Plus className="h-6 w-6 sm:h-8 sm:w-8" />
            </div>
            <h1 className="pm-heading-1 text-lg sm:text-2xl mb-1 text-gray-900 dark:text-white">Quick Add Contact</h1>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="absolute right-2 top-0 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {step === 'success' ? (
          <div className="flex flex-col items-center py-8 gap-3">
            <CheckCircle2 className="w-12 h-12 text-green-500" />
            <p className={`text-lg font-medium ${text}`}>Contact Created</p>
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}

            {/* ---- Contact Details ---- */}
            <div className={`${cardBg} rounded-lg p-4 space-y-3`}>
              <div className="flex items-center gap-2 mb-1">
                <Building2 className="w-4 h-4 text-blue-500" />
                <span className={`text-sm font-semibold ${text}`}>Contact Details</span>
                <span className="text-red-400 text-xs">*required</span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className={labelCls}>Company / Contact Name *</label>
                  <input
                    className={inputCls}
                    placeholder="e.g. Acme Engineering (Pty) Ltd"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    autoFocus
                  />
                </div>

                <div>
                  <label className={labelCls}>Contact Type</label>
                  <select
                    className={selectCls}
                    value={contactType}
                    onChange={(e) => setContactType(e.target.value)}
                  >
                    {availableTypes.map((t) => (
                      <option key={t} value={t}>
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelCls}>Phone</label>
                  <input
                    className={inputCls}
                    placeholder="+27 12 345 6789"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                  />
                </div>

                <div className="col-span-2">
                  <label className={labelCls}>Email</label>
                  <input
                    className={inputCls}
                    type="email"
                    placeholder="accounts@company.co.za"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                  />
                </div>

                <div>
                  <label className={labelCls}>Tax Number</label>
                  <input
                    className={inputCls}
                    placeholder="VAT number"
                    value={taxNumber}
                    onChange={(e) => setTaxNumber(e.target.value)}
                  />
                </div>

                <div>
                  <label className={labelCls}>Registration Number</label>
                  <input
                    className={inputCls}
                    placeholder="Company reg"
                    value={registrationNumber}
                    onChange={(e) => setRegistrationNumber(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* ---- Supplier Payment Terms (COD vs Terms) ---- */}
            {contactType === 'supplier' && (
              <div className={`${cardBg} rounded-lg p-4 space-y-3`}>
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard className="w-4 h-4 text-amber-500" />
                  <span className={`text-sm font-semibold ${text}`}>Payment Terms</span>
                  <span className={`text-xs ${textMuted}`}>
                    {paymentTermsMode === 'cod'
                      ? 'DIN → EXP (cash purchase)'
                      : 'DIN → BIL (supplier bill)'}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setPaymentTermsMode('cod')}
                    className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition ${
                      paymentTermsMode === 'cod'
                        ? isDark
                          ? 'bg-amber-600 border-amber-500 text-white'
                          : 'bg-amber-500 border-amber-400 text-white'
                        : isDark
                          ? 'bg-gray-700 border-gray-600 text-gray-300 hover:border-gray-500'
                          : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    COD (Cash on Delivery)
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentTermsMode('terms')}
                    className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition ${
                      paymentTermsMode === 'terms'
                        ? isDark
                          ? 'bg-blue-600 border-blue-500 text-white'
                          : 'bg-blue-500 border-blue-400 text-white'
                        : isDark
                          ? 'bg-gray-700 border-gray-600 text-gray-300 hover:border-gray-500'
                          : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    Terms (Credit Account)
                  </button>
                </div>
                {paymentTermsMode === 'terms' && (
                  <div>
                    <label className={labelCls}>Payment Terms Days</label>
                    <select
                      className={selectCls}
                      value={paymentTermsDays}
                      onChange={(e) => setPaymentTermsDays(Number(e.target.value))}
                    >
                      <option value={7}>7 days</option>
                      <option value={14}>14 days</option>
                      <option value={30}>30 days</option>
                      <option value={60}>60 days</option>
                      <option value={90}>90 days</option>
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* ---- Contact Person (collapsible) ---- */}
            <div className={`${cardBg} rounded-lg overflow-hidden`}>
              <button
                type="button"
                className={`w-full flex items-center justify-between p-4 ${text} hover:opacity-80 transition`}
                onClick={() => setShowPerson(!showPerson)}
              >
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-emerald-500" />
                  <span className="text-sm font-semibold">Contact Person</span>
                  <span className={`text-xs ${textMuted}`}>(optional)</span>
                </div>
                {showPerson ? (
                  <ChevronUp className={`w-4 h-4 ${textMuted}`} />
                ) : (
                  <ChevronDown className={`w-4 h-4 ${textMuted}`} />
                )}
              </button>

              {showPerson && (
                <div className="px-4 pb-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>First Name</label>
                      <input
                        className={inputCls}
                        placeholder="John"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Last Name</label>
                      <input
                        className={inputCls}
                        placeholder="Smith"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Email</label>
                      <input
                        className={inputCls}
                        type="email"
                        placeholder="john@company.co.za"
                        value={personEmail}
                        onChange={(e) => setPersonEmail(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Phone</label>
                      <input
                        className={inputCls}
                        placeholder="+27 82 123 4567"
                        value={personPhone}
                        onChange={(e) => setPersonPhone(e.target.value)}
                      />
                    </div>
                    <div className="col-span-2">
                      <label className={labelCls}>Job Title</label>
                      <input
                        className={inputCls}
                        placeholder="Operations Manager"
                        value={jobTitle}
                        onChange={(e) => setJobTitle(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ---- Location (collapsible) ---- */}
            <div className={`${cardBg} rounded-lg overflow-hidden`}>
              <button
                type="button"
                className={`w-full flex items-center justify-between p-4 ${text} hover:opacity-80 transition`}
                onClick={() => setShowLocation(!showLocation)}
              >
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-orange-500" />
                  <span className="text-sm font-semibold">Location</span>
                  <span className={`text-xs ${textMuted}`}>(optional)</span>
                </div>
                {showLocation ? (
                  <ChevronUp className={`w-4 h-4 ${textMuted}`} />
                ) : (
                  <ChevronDown className={`w-4 h-4 ${textMuted}`} />
                )}
              </button>

              {showLocation && (
                <div className="px-4 pb-4 space-y-3">
                  <div>
                    <label className={labelCls}>Location Name *</label>
                    <input
                      className={inputCls}
                      placeholder="e.g. Head Office, Warehouse, Site A"
                      value={locationName}
                      onChange={(e) => setLocationName(e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Location Type</label>
                      <select
                        className={selectCls}
                        value={locationType}
                        onChange={(e) => setLocationType(e.target.value)}
                      >
                        <option value="office">Office</option>
                        <option value="warehouse">Warehouse</option>
                        <option value="site">Site</option>
                        <option value="factory">Factory</option>
                        <option value="residential">Residential</option>
                        <option value="retail">Retail</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Geofence Radius (m)</label>
                      <input
                        className={inputCls}
                        type="number"
                        min={10}
                        max={10000}
                        value={radiusMeters}
                        onChange={(e) => setRadiusMeters(Number(e.target.value) || 100)}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>Address (search to get coordinates)</label>
                    <LocationAutocomplete
                      value={locationSearch}
                      onChange={setLocationSearch}
                      onPlaceSelect={(data) => {
                        setLocationData(data);
                        setLocationSearch(data.address);
                        if (!locationName.trim()) {
                          setLocationName(data.name || companyName.trim());
                        }
                      }}
                      onClear={() => {
                        setLocationData(null);
                        setLocationSearch('');
                      }}
                      isDark={isDark}
                      placeholder="Search address or place..."
                    />
                    {locationData && (
                      <div className={`mt-2 text-xs ${textMuted} flex items-center gap-1`}>
                        <MapPin className="w-3 h-3 text-green-500" />
                        <span>
                          {locationData.address}
                          {' '}({parseFloat(locationData.lat).toFixed(5)}, {parseFloat(locationData.lng).toFixed(5)})
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* ---- Actions ---- */}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className={`px-4 py-2 text-sm rounded-lg border ${border} ${text} hover:opacity-80 transition`}
                onClick={handleClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2"
                onClick={handleSubmit}
                disabled={saving || !isValid}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Create Contact
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
